import {AsyncInlineError} from '@s-ui/decorators'

import MotorFetcherFactory from '../../fetcher/Fetchers/factory.js'
import {UserAlertEntity} from '../Entities/UserAlertEntity.js'
import {GetAlertUserAlertsError} from '../Errors/GetAlertUserAlertsError.js'
import {GetMatchesUserAlertsError} from '../Errors/GetMatchesUserAlertsError.js'
import {UserAlertsMappersFactory} from '../Mappers/factory.js'
import {HTTPMatchesResponseToMatchesListValueObjectMapper} from '../Mappers/HTTPMatchesResponseToMatchesListValueObjectMapper.js'
import UserAlertsRepository from './UserAlertsRepository.js'

/**
 * @interface
 */
class HTTPUserAlertsRepository extends UserAlertsRepository {
  /**
   * @constructor
   * @param {Object} deps
   * @param {Object} deps.config
   * @param {Fetcher} deps.fetchOrFail
   * @param {Object} deps.hTTPUserAlertsListResponseToUserAlertsValueObjectMapper
   * @param {Function} deps.getUserAlertsError
   * @param {Function} deps.deleteUserAlertsError
   * @param {Function} deps.updateUserAlertsError
   * @param {Function} hTTPNotificationsToUnseenSavedSearchMatchesMapper
   */

  constructor({
    config,
    fetchOrFail,
    fromUserAlertRawDataToUserAlertEntityMapper,
    hTTPUserAlertsListResponseToUserAlertsValueObjectMapper,
    getUserAlertError,
    getUserAlertsError,
    deleteUserAlertsError,
    updateUserAlertsError,
    userAlertEntity,
    hTTPNotificationsToUnseenSavedSearchMatchesMapper,
    httpCreateAlertResponseJSONtoAlertEntityMapper,
    duplicatedUserAlertError,
    maxLimitReachedUserAlertError,
    userNotLoggedInUserErrorFactory,
    searchHasNoFiltersUserAlertError,
    hTTPMatchesResponseToMatchesListValueObjectMapper
  }) {
    super()
    this._config = config
    this._deleteUserAlertsError = deleteUserAlertsError
    this._fetchOrFail = fetchOrFail
    this._duplicatedUserAlertError = duplicatedUserAlertError
    this._maxLimitReachedUserAlertError = maxLimitReachedUserAlertError
    this._userNotLoggedInUserErrorFactory = userNotLoggedInUserErrorFactory
    this._getUserAlertError = getUserAlertError
    this._getUserAlertsError = getUserAlertsError
    this._userAlertEntity = userAlertEntity
    this._fromUserAlertRawDataToUserAlertEntityMapper = fromUserAlertRawDataToUserAlertEntityMapper
    this._hTTPUserAlertsListResponseToUserAlertsValueObjectMapper =
      hTTPUserAlertsListResponseToUserAlertsValueObjectMapper
    this._updateUserAlertsError = updateUserAlertsError
    this._hTTPNotificationsToUnseenSavedSearchMatchesMapper = hTTPNotificationsToUnseenSavedSearchMatchesMapper
    this._httpCreateAlertResponseJSONtoAlertEntityMapper = httpCreateAlertResponseJSONtoAlertEntityMapper
    this._defaultUnreadMessages = this._config.get('notifications').DEFAULT_UNREAD_MESSAGES
    this._searchHasNoFiltersUserAlertError = searchHasNoFiltersUserAlertError
    this._hTTPMatchesResponseToMatchesListValueObjectMapper = hTTPMatchesResponseToMatchesListValueObjectMapper
  }

  static create({config}) {
    return new HTTPUserAlertsRepository({
      config,
      fetchOrFail: MotorFetcherFactory.fetchOrFail({
        config
      }),
      fromUserAlertRawDataToUserAlertEntityMapper: UserAlertsMappersFactory.fromUserAlertRawDataToUserAlertEntityMapper(
        {
          config
        }
      ),
      hTTPMatchesResponseToMatchesListValueObjectMapper: HTTPMatchesResponseToMatchesListValueObjectMapper.create({
        config
      })
    })
  }

  /**
   * @method
   * @return {Promise}
   */
  @AsyncInlineError()
  async createAlert({adId, filters, userId}) {
    const {GENERIC, USER_ERRORS} = this._config.get('COMMON').ERRORS
    const {CONFLICT, UNPROCESSABLE_CONTENT} = this._config.get('COMMON').HTTP_STATUS_CODES
    const {MAX_ALERTS_LIMIT_REACHED_ERROR_CODE} = this._config.get('COMMON').SEARCH_SAVE

    const notAvailableFilters = UserAlertEntity.getNotAvailableFilters({filters})

    const [error, response] = await this._fetchOrFail
      .withErrorsFactories({
        [UNPROCESSABLE_CONTENT]: () => {
          return this._searchHasNoFiltersUserAlertError({
            message: 'Search has no filters'
          })
        },
        [GENERIC]: () => {
          return this._getUserAlertError('User alert can not be resolved')
        },
        [USER_ERRORS]: () => {
          return this._userNotLoggedInUserErrorFactory({
            message: 'To create an alert you must be logged in',
            response
          })
        },
        [CONFLICT]: (...errorResponse) => {
          // HTTP 409 CONFLICT is returned for:
          // - Duplicate alert error (user already has this alert)
          // - Max alerts limit reached error
          // Check the response body's error code to distinguish between them.
          const {code} = errorResponse?.[0]?.response?.data || {}

          if (code === MAX_ALERTS_LIMIT_REACHED_ERROR_CODE) {
            return this._maxLimitReachedUserAlertError({
              code: MAX_ALERTS_LIMIT_REACHED_ERROR_CODE,
              message: 'You have reached to the maximum amount of alerts that can be created'
            }).getError()
          }
          return this._duplicatedUserAlertError({
            notAvailableFilters,
            message: 'You cannot create an alert that is already created'
          }).getError()
        }
      })
      .post(
        `${this._config.get('API_URL')}/v2/users/${userId}/alerts`,
        {
          filters,
          adId,
          activeEmail: true,
          activePush: true
        },
        {headers: {Accept: 'application/json'}}
      )

    if (error) {
      return Promise.reject(error)
    }

    const {data} = response

    return this._userAlertEntity({...data, notAvailableFilters})
  }

  @AsyncInlineError()
  async getUserAlert({alertId, userId}) {
    const {GENERIC} = this._config.get('COMMON').ERRORS
    const {BAD_REQUEST, UNAUTHORIZED, FORBIDDEN, NOT_FOUND} = this._config.get('COMMON').HTTP_STATUS_CODES

    const [error, response] = await this._fetchOrFail
      .withErrorsFactories({
        [BAD_REQUEST]: () => GetAlertUserAlertsError.createInvalidParametersError(),
        [FORBIDDEN]: () => GetAlertUserAlertsError.createNotAuthorizedError(),
        [GENERIC]: () => GetAlertUserAlertsError.createGenericError(),
        [NOT_FOUND]: () => GetAlertUserAlertsError.createAlertNotFoundError(),
        [UNAUTHORIZED]: () => GetAlertUserAlertsError.createNotAuthenticatedError()
      })
      .get(`${this._config.get('API_URL')}/v2/users/${userId}/alerts/${alertId}`)

    if (error) {
      return Promise.reject(error)
    }

    const {data} = response

    return this._fromUserAlertRawDataToUserAlertEntityMapper.map(data)
  }

  @AsyncInlineError()
  async getUserAlerts({userId}) {
    const {GENERIC} = this._config.get('COMMON').ERRORS
    const [error, response] = await this._fetchOrFail
      .withErrorsFactories({
        [GENERIC]: () => {
          return this._getUserAlertsError('User alerts can not be resolved')
        }
      })
      .get(`${this._config.get('API_URL')}/v2/users/${userId}/alerts`, {
        headers: {Accept: 'application/json'}
      })

    if (error) {
      return Promise.reject(error)
    }

    const {data} = response

    const userAlerts = this._hTTPUserAlertsListResponseToUserAlertsValueObjectMapper.map({
      data
    })

    return userAlerts
  }

  @AsyncInlineError()
  async updateChannelUserAlert({userId, alertId, activeEmail, activePush}) {
    const {GENERIC} = this._config.get('COMMON').ERRORS

    const [error, response] = await this._fetchOrFail
      .withErrorsFactories({
        [GENERIC]: ({message, response}) => {
          return this._updateUserAlertsError({
            message,
            response
          }).getError(response)
        }
      })
      .put(
        `${this._config.get('API_URL')}/v2/users/${userId}/alerts/${alertId}/channels`,
        {activeEmail, activePush},
        {
          headers: {'content-type': 'application/json'}
        }
      )
    if (error) {
      return Promise.reject(error)
    }

    return response
  }

  @AsyncInlineError()
  async updateFrequencyUserAlert({userId, alertId, frequencyData}) {
    const {GENERIC} = this._config.get('COMMON').ERRORS

    const [error, response] = await this._fetchOrFail
      .withErrorsFactories({
        [GENERIC]: ({message, response}) => {
          return this._updateUserAlertsError({
            message,
            response
          }).getError(response)
        }
      })
      .put(`${this._config.get('API_URL')}/v2/users/${userId}/alerts/${alertId}/frequency`, frequencyData, {
        headers: {'content-type': 'application/json'}
      })
    if (error) {
      return Promise.reject(error)
    }

    return response
  }

  @AsyncInlineError()
  async updateUserAlert({userId, alertId, parsedUserAlert}) {
    const {GENERIC} = this._config.get('COMMON').ERRORS
    const {UNPROCESSABLE_ENTITY} = this._config.get('COMMON').HTTP_STATUS_CODES

    const [error, response] = await this._fetchOrFail
      .withErrorsFactories({
        [GENERIC]: ({message, response}) => {
          return this._updateUserAlertsError({
            message,
            response
          }).getError(response)
        },
        [UNPROCESSABLE_ENTITY]: ({message, response}) => {
          const {code} = response.data
          switch (code) {
            case '100':
              throw this._searchHasNoFiltersUserAlertError({
                message,
                response
              }).getErrorData()
            default:
              throw this._getUserAlertError('User alert can not be resolved')
          }
        }
      })
      .put(`${this._config.get('API_URL')}/v2/users/${userId}/alerts/${alertId}`, parsedUserAlert, {
        headers: {'content-type': 'application/json'}
      })
    if (error) {
      return Promise.reject(error)
    }

    return response
  }

  @AsyncInlineError()
  async deleteUserAlert({userId, alertId}) {
    const {GENERIC} = this._config.get('COMMON').ERRORS
    const [error, response] = await this._fetchOrFail
      .withErrorsFactories({
        [GENERIC]: () => {
          return this._deleteUserAlertsError('User alert can not be deleted')
        }
      })
      .delete(`${this._config.get('API_URL')}/users/${userId}/alerts/${alertId}`)

    if (error) {
      return Promise.reject(error)
    }

    return response
  }

  unsubscribeAlert({alertId, unsubscribeToken, userId}) {
    const baseUrl = `${this._config.get('API_URL')}/${this._config.get('TENANT')}`

    const url = unsubscribeToken
      ? // unsubscribeToken param is needed by legacy MOTOS endpoint
        `${baseUrl}/unsubscribe/alert/${userId}/${alertId}?unsubscribetoken=${unsubscribeToken}`
      : `${baseUrl}/alerts/unsubscribe/${userId}/savedsearch/${alertId}`

    // Also, the legacy MOTOS endpoint has a POST method
    const unsubscribePromise = unsubscribeToken ? this._fetchOrFail.post(url) : this._fetchOrFail.delete(url)

    return unsubscribePromise.then(([error]) => {
      if (error) {
        throw error
      }
      return Promise.resolve()
    })
  }

  /**
   * @return {Promise<Number>}
   */
  @AsyncInlineError()
  async getUnseenSavedSearchMatches({userId, alertId = ''}) {
    const [error, response] = await this._fetchOrFail
      .withErrorsFactories()
      .get(`${this._config.get('API_URL')}/users/${userId}/matchcounters${alertId ? `/${alertId}` : ''}`, {
        headers: {'X-Enable-Time-Ranges': true}
      })

    if (error) return Promise.reject(error)

    const userMatchCounters = this._hTTPNotificationsToUnseenSavedSearchMatchesMapper.map({
      data: response.data
    })

    return userMatchCounters
  }

  /**
   * @return {Promise<Number>}
   */
  async resetUnseenSavedSearchMatchesById({userId, alertId}) {
    const [error, response] = await this._fetchOrFail
      .withErrorsFactories()
      .post(`${this._config.get('API_URL')}/users/${userId}/matchcounters/${alertId}`)

    if (error) {
      return this._defaultUnreadMessages
    }

    return response.data
  }

  @AsyncInlineError()
  async getMatchesByAlertId({alertId, userId, search = {}}) {
    const {GENERIC} = this._config.get('COMMON').ERRORS
    const {BAD_REQUEST, UNAUTHORIZED, FORBIDDEN, NOT_FOUND} = this._config.get('COMMON').HTTP_STATUS_CODES

    const [error, response] = await this._fetchOrFail
      .withErrorsFactories({
        [BAD_REQUEST]: () => GetMatchesUserAlertsError.createInvalidParametersError(),
        [FORBIDDEN]: () => GetMatchesUserAlertsError.createNotAuthorizedError(),
        [GENERIC]: () => GetMatchesUserAlertsError.createGenericError(),
        [NOT_FOUND]: () => GetMatchesUserAlertsError.createSavedSearchNotFoundError(),
        [UNAUTHORIZED]: () => GetMatchesUserAlertsError.createNotAuthenticatedError()
      })
      .get(`${this._config.get('API_URL')}/v3/users/${userId}/alerts/${alertId}/matches`, {
        headers: {'X-Enable-Time-Ranges': true}
      })

    if (error) return Promise.reject(error)

    const {data} = response

    return this._hTTPMatchesResponseToMatchesListValueObjectMapper.map({items: data.items, search})
  }
}

export default HTTPUserAlertsRepository
